import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Grid, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Button, SubmitButton } from '../../../../common/button';
import { SelectFieldFormik, SelectFieldFormikWithMore } from '../../../../common/select_field';
import { getGenerateWarehouseDocumentTypeRequestOptions, getWarehouseName } from '../../../../common/utils/warehouse_utils';
import { GenerateWarehouseDocumentTypeRequestEnum } from '../../../../../core/enums/inventory/warehouse_document';
import ThreeLevelSteps from '../../../common/steps';
import { getAllFoodPartnersState } from '../../../../../features/food_partner/get_all_food_partners';
import DatePickerFormik from '../../../../common/date_picker';
import { parseDateString } from '../../../../../core/utils/date_utils';
import { getAllWarehousesState } from '../../../../../features/inventory/get_all_warehouses';
import { yesNoOptions } from '../../../../common/form_utils';
import RadioGroupFormik from '../../../../common/radio_group';
import { WarehouseTypeEnum } from "../../../../../core/enums/inventory/warehouse";
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';
import { ROUTE_WAREHOUSE_DOCUMENTS } from '../../../../../core/constants';

const GeneralDataForm = ({ form, onSubmitGeneralData }) => {
    const navigate = useNavigate();
    const getFoodPartners = useSelector(getAllFoodPartnersState);
    const getAllWarehouses = useSelector(getAllWarehousesState);

    const initialValues = {
        type: form?.type || '',
        foodPartnerId: form?.foodPartnerId || '',
        sourceWarehouseId: form?.sourceWarehouseId || '',
        confirmStocking: form?.confirmStocking || false,
        deliveryDate: form?.deliveryDate || ''
    };

    const schema = Yup.object().shape({
        type: Yup.string().required(),
        foodPartnerId: Yup.number().when('type', {
            is: (type) => type === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value,
            then: Yup.number().notRequired(),
            otherwise: Yup.number().required()
        }),
        sourceWarehouseId: Yup.number().when('type', {
            is: (type) => type === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value,
            then: Yup.number().required(),
            otherwise: Yup.number().notRequired()
        }),
        confirmStocking: Yup.boolean().when('type', {
            is: (type) => type === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value,
            then: Yup.boolean().required(),
            otherwise: Yup.boolean().notRequired()
        }),
        deliveryDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required()
    });

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmitGeneralData }>
            { (formik) => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <Stack spacing={ 3 }>
                            <ThreeLevelSteps firstStep/>

                            <SectionPaper title="Dane ogólne">
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                        <SelectFieldFormik
                                            name="type"
                                            label="Wariant dokumentu"
                                            options={ getGenerateWarehouseDocumentTypeRequestOptions }
                                            required
                                            onChange={ (value) => {
                                                value === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value && formik.setFieldValue('foodPartnerId', '');
                                                value !== GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value && formik.setFieldValue('sourceWarehouseId', '');
                                                value !== GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value && formik.setFieldValue('confirmDelivery', '');
                                            } }
                                        />
                                    </Grid>

                                    {
                                        (
                                            formik.values.type === GenerateWarehouseDocumentTypeRequestEnum.PZ_DOCUMENT.value
                                            || formik.values.type === GenerateWarehouseDocumentTypeRequestEnum.PZ_AND_MM_DOCUMENTS.value
                                            || formik.values.type === GenerateWarehouseDocumentTypeRequestEnum.PW_DOCUMENT.value
                                        ) && (
                                            <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                                <SelectFieldFormik
                                                    name="foodPartnerId"
                                                    label="Dostawca"
                                                    options={ getFoodPartners?.data?.map(getFoodPartner => ({
                                                        value: getFoodPartner?.id,
                                                        label: getFoodPartner?.name
                                                    })) }
                                                    required
                                                />
                                            </Grid>
                                        )
                                    }

                                    {
                                        formik.values.type === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value && (
                                            <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                                <SelectFieldFormikWithMore
                                                    name="sourceWarehouseId"
                                                    label="Magazyn źródłowy"
                                                    defaultOptions={ getAllWarehouses?.data?.filter(warehouse => warehouse?.type !== WarehouseTypeEnum.POD.value || warehouse?.pod?.serviceId != null).map(warehouse => ({
                                                        value: warehouse?.internalId,
                                                        label: getWarehouseName(warehouse)
                                                    })) }
                                                    extraOptions={ getAllWarehouses?.data?.filter(warehouse => warehouse?.type === WarehouseTypeEnum.POD.value && warehouse?.pod?.serviceId == null).map(warehouse => ({
                                                        value: warehouse?.internalId,
                                                        label: getWarehouseName(warehouse)
                                                    })) }
                                                    required
                                                />
                                            </Grid>
                                        )
                                    }

                                    <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                        <DatePickerFormik
                                            name="deliveryDate"
                                            label="Data dostawy"
                                            required
                                            InputLabelProps={ { shrink: true } }
                                        />
                                    </Grid>

                                    {
                                        formik.values.type === GenerateWarehouseDocumentTypeRequestEnum.MM_DOCUMENTS.value && (
                                            <Grid item xs={ 12 } md={ 6 } lg={ 4 }>
                                                <RadioGroupFormik
                                                    name="confirmStocking"
                                                    label="Potwierdź zatowarowanie"
                                                    options={ yesNoOptions }
                                                />
                                            </Grid>
                                        )
                                    }
                                </Grid>
                            </SectionPaper>
                        </Stack>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                            <Button variant="outlined"
                                    onClick={ () => navigate(ROUTE_WAREHOUSE_DOCUMENTS) }>Wróć</Button>
                            <SubmitButton>Dalej</SubmitButton>
                        </Box>
                    </BottomActionBar>
                </Box>
            ) }
        </Formik>
    );
};

export default GeneralDataForm;